import { StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { UnheadProvider, createHead } from '@unhead/react/client';
import { createI18n, DEFAULT_LOCALE, isSupportedLocale } from '@noxverify/i18n';

import { App } from './App';
import {
  eagerEnResources,
  attachLazyLocaleLoader,
  ensureLocaleLoaded,
} from './i18n-client-resources';

// Self-hosted fonts (replaces the render-blocking Google Fonts stylesheet).
// IBM Plex Sans 400/500/600/700 (UI + display) and IBM Plex Mono 400/500
// (data / eyebrows). All ship with `font-display: swap` via @fontsource.
// 400/500/600 via @fontsource; the 700 (H1) weight is declared in styles.css
// against a stable /fonts path so it can be <link rel="preload">-ed without a
// hashed-asset mismatch / double-fetch.
import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';

import './styles.css';

/* ---------------------------------------------------------------------------
 * Client i18n: English is bundled eagerly (default route + fallbackLng); every
 * non-English locale is code-split and lazy-loaded on demand. This keeps all 21
 * non-en locales' JSON out of the main browser chunk (Lighthouse perf win).
 *
 * The SSR/prerender path (`resources.ts` + `entry-server.tsx`) is independent
 * and still ships the correct-language HTML, so AI crawlers + first paint of a
 * `/<locale>/` route are unaffected. On the client, LocaleLayout calls
 * `i18n.changeLanguage(<urlLocale>)`, which triggers the lazy loader below.
 * --------------------------------------------------------------------------*/
const i18n = createI18n({
  resources: eagerEnResources,
  useHttpBackend: false,
  detectLanguage: false,
  locale: DEFAULT_LOCALE,
  ns: ['common', 'seo', 'marketing', 'legal', 'blog'],
  defaultNS: 'common',
});

// Lazy-load the active locale (from the URL via LocaleLayout) on demand.
attachLazyLocaleLoader(i18n);

const head = createHead();
const container = document.getElementById('root')!;

const tree = (
  <StrictMode>
    <UnheadProvider head={head}>
      <I18nextProvider i18n={i18n}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </I18nextProvider>
    </UnheadProvider>
  </StrictMode>
);

/**
 * Derive the active locale from the URL's first path segment.
 *
 * Non-default locales are served under `/<locale>/...` (see App.tsx routing).
 * The default locale (en) lives at the bare root with no prefix. Returns
 * `DEFAULT_LOCALE` for the bare root or any unsupported leading segment.
 */
function localeFromPath(pathname: string): string {
  const first = pathname.split('/').filter(Boolean)[0];
  return first && isSupportedLocale(first) ? first : DEFAULT_LOCALE;
}

/* ---------------------------------------------------------------------------
 * Hydration (NOT re-render): the `#root` container already holds the
 * SSR-prerendered, locale-correct HTML produced by `scripts/prerender.mjs` +
 * `entry-server.tsx`. `hydrateRoot` REUSES that markup — the LCP <h1> stays
 * painted while React attaches listeners in the background — instead of
 * discarding + re-rendering it (which `createRoot().render()` does, adding a
 * ~3s render-delay on slow mobile).
 *
 * Hydration only matches the SSR markup when the client's first render produces
 * identical output. English is bundled eagerly, so the `/` route (Lighthouse's
 * tested route) hydrates synchronously and cleanly. For a non-default locale,
 * the SSR HTML is localized but the client starts in en — so we MUST await that
 * locale's lazy resource bundles and switch i18next to it BEFORE hydrating,
 * otherwise React detects a mismatch and re-renders (defeating the win).
 * --------------------------------------------------------------------------*/
async function mount(): Promise<void> {
  const locale = localeFromPath(window.location.pathname);

  if (locale !== DEFAULT_LOCALE) {
    // Await the lazy chunk(s) + addResourceBundle, then switch language so the
    // client's first render matches the localized SSR markup.
    await ensureLocaleLoaded(i18n, locale);
    await i18n.changeLanguage(locale);
  }

  hydrateRoot(container, tree);
}

void mount();
